import React, { Fragment, useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
// import { RadioGroup, RadioGroupItem } from './ui/RadioGroup';
import svgs from './ui/svgs';

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onProcedureChange: (value: string) => void;
  onSortChange: (value: string) => void;
  procedureOptions: ProcedureOption[];
}

const sortOptions = [
  { value: 'date-desc', label: 'Newest to Oldest' },
  { value: 'date-asc', label: 'Oldest to Newest' }
];

interface ProcedureOption {
  value: string;
  label: string;
}

interface ProcedureDropdownProps {
  procedureOptions: ProcedureOption[];
  selectedValue: string;
  onChange: (value: string) => void;
}

const PROCEDURE_OPTIONS_ALL = 'all';

function ProcedureDropdown({ procedureOptions, selectedValue, onChange }: ProcedureDropdownProps)
  : React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Prefix procedureOptions with an 'All' option if not already present
  const displayOptions = [{ value: PROCEDURE_OPTIONS_ALL, label: 'All Procedures' }, ...procedureOptions];
  const selectedOption = displayOptions.find(opt => opt.value === selectedValue) || displayOptions[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string): void => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="content-stretch flex flex-col items-start relative shrink-0 w-full" data-name="Dropdowns">
      {/* Label */}
      <div className="box-border content-stretch flex h-[26px] items-center pb-[2px] pt-0 px-0 relative shrink-0 w-full" data-name="Label + Icon">
        <div className="content-stretch flex gap-[4px] items-end relative shrink-0" data-name="Label">
          <p 
            className="leading-[normal] text-nowrap whitespace-pre" 
            style={{ 
              fontFamily: 'Open Sans',
              fontSize: 'var(--text-caption)',
              color: 'var(--medium-emphasis-font)',
              fontVariationSettings: "'wdth' 100"
            }}
          >
            Procedure Type
          </p>
        </div>
      </div>

      {/* Dropdown Input */}
      <div className="content-stretch flex flex-col h-[44px] items-start overflow-clip relative rounded-[4px] shrink-0 w-full" data-name="Input">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="bg-white h-[44px] relative rounded-[4px] shrink-0 w-full"
          data-name="Contents"
          data-testid="procedure-dropdown-button"
        >
          <div className="flex flex-row items-center overflow-clip rounded-[inherit] size-full">
            <div className="box-border content-stretch flex gap-[10px] h-[44px] items-center px-[10px] py-0 relative w-full">
              <div className="basis-0 content-stretch flex grow items-center min-h-px min-w-px relative shrink-0" data-name="Icon-Text-stack">
                <div className="basis-0 grow min-h-px min-w-px relative shrink-0" data-name="Text">
                  <div className="overflow-clip rounded-[inherit] size-full">
                    <div className="box-border content-stretch flex items-start pb-[7px] pt-[5px] px-[2px] w-full">
                      <p 
                        className="leading-[normal] text-left line-clamp-2 overflow-hidden text-ellipsis" 
                        style={{ 
                          fontFamily: 'Open Sans',
                          fontSize: 'var(--text-h4)',
                          color: 'var(--high-emphasis-font)',
                          fontVariationSettings: "'wdth' 100",
                          lineHeight: '1.2',
                          maxHeight: '2.4em'
                        }}
                      >
                        {selectedOption?.label}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="box-border content-stretch flex gap-[4px] items-center justify-end pl-[2px] pr-0 py-[6px] relative shrink-0" data-name="Icon End">
                <div className="relative shrink-0 size-[20px]" data-name="Chevron Down Icon">
                  <ChevronDown 
                    className="absolute inset-0 transition-transform" 
                    size={20}
                    style={{ 
                      color: 'var(--medium-emphasis-font)',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  />
                </div>
              </div>

            </div>
          </div>
          <div 
            aria-hidden="true" 
            className="absolute border border-solid inset-0 pointer-events-none rounded-[4px]" 
            style={{ borderColor: isOpen ? 'var(--primary)' : 'var(--border)' }}
          />
        </button>
        <div 
          className="absolute bottom-0 h-px left-0 right-0 rounded-[4px]" 
          data-name="Thin underline" 
          style={{ backgroundColor: isOpen ? 'var(--primary)' : 'var(--medium-emphasis-font)' }}
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute top-[72px] left-0 right-0 z-50 overflow-y-auto max-h-[200px] md:max-h-[300px]"
          style={{ 
            backgroundColor: 'var(--card)',
            boxShadow: '0px 0px 20px 0px rgba(0,0,0,0.2)'
          }}
        >
          {displayOptions.map((option, index) => (
            <button
              data-testid={`procedure-option-${index}`}
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className="w-full h-[44px] px-[10px] flex items-center transition-colors hover:bg-neutral-100"
              style={{ 
                backgroundColor: selectedValue === option.value ? 'rgba(0,0,0,0.05)' : 'var(--card)',
                borderRadius: 0
              }}
              onMouseEnter={(e) => {
                if (selectedValue !== option.value) {
                  e.currentTarget.style.backgroundColor = '#eee';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedValue !== option.value) {
                  e.currentTarget.style.backgroundColor = 'var(--card)';
                }
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.backgroundColor = '#E0E0E0';
              }}
              onMouseUp={(e) => {
                if (selectedValue !== option.value) {
                  e.currentTarget.style.backgroundColor = '#eee';
                } else {
                  e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                }
              }}
            >
              <p 
                className="leading-[normal] text-left" 
                style={{ 
                  fontFamily: 'Open Sans',
                  fontSize: 'var(--text-h4)',
                  fontWeight: 'var(--font-weight-normal)',
                  color: 'var(--high-emphasis-font)',
                  fontVariationSettings: "'wdth' 100"
                }}
              >
                {option.label} 
              </p>
            </button>
          ))}
        </div>

      )}
    </div>
  );
}

function RadioOption({ label, isSelected }:
  { label: string; isSelected: boolean }): React.JSX.Element {
  return (
    <div className="content-stretch flex gap-[4px] items-center relative shrink-0 w-full" data-name="Checkbox">
      <div className="box-border content-stretch flex items-start p-[12px] relative shrink-0" data-name="Checkbox elements">
        <div 
          className="relative rounded-[20px] shrink-0 size-[20px]" 
          data-name={isSelected ? 'Icon' : 'Background'}
          style={{ backgroundColor: isSelected ? 'var(--primary)' : 'transparent' }}
        >
          {isSelected ? (
            <div className="absolute h-[6px] left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%] w-[8px]" data-name="Shape">
              <div className="absolute inset-0">
                <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 8 6">
                  <path d={svgs.p340332c0} fill="white" id="Shape" />
                </svg>
              </div>
            </div>
          ) : (
            <div 
              aria-hidden="true" 
              className="absolute border border-solid inset-0 pointer-events-none rounded-[20px]"
              style={{ borderColor: 'var(--high-emphasis-font)' }}
            />
          )}
        </div>
      </div>
      <div className="basis-0 grow min-h-px min-w-px relative shrink-0" data-name="Text wrapper for offset">
        <div className="size-full">
          <div className="box-border content-stretch flex items-start pb-[7px] pl-0 pr-[12px] pt-[5px] relative w-full">
            <div className="basis-0 content-stretch flex gap-[4px] grow items-end min-h-px min-w-px relative shrink-0" data-name="Label">
              <p 
                className="basis-0 grow leading-[normal] min-h-px min-w-px relative shrink-0" 
                style={{ 
                  fontFamily: 'Open Sans',
                  fontSize: 'var(--text-base)',
                  color: isSelected ? 'var(--high-emphasis-font)' : 'var(--medium-emphasis-font)',
                  fontVariationSettings: "'wdth' 100"
                }}
              >
                {label}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function FilterPanel(
  { isOpen, onClose, onProcedureChange, onSortChange, procedureOptions }
  : FilterPanelProps): React.JSX.Element {
  const [sortValue, setSortValue] = useState('date-desc');
  const [procedureValue, setProcedureValue] = useState('all');
  const panelRef = useRef<HTMLDivElement>(null);

  const handleProcedureChange = (value: string): void => {
    setProcedureValue(value);
    onProcedureChange(value);
  };

  const handleSortChange = (value: string): void => {
    setSortValue(value);
    onSortChange(value);
  };

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when panel is open
      document.body.style.overflow = 'hidden';
    }

    return (): void => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <Fragment>
      {/* Mobile Version - Bottom slide-up panel (up to 744px) */}
      <div 
        ref={panelRef}
        data-testid="filter-panel-mobile"
        className="fixed bottom-0 left-0 right-0 z-50 lg:hidden transition-transform duration-300 ease-out"
        style={{ 
          transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          height: '441px',
          maxHeight: '441px'
        }}
        data-name="Sort By Dropdown Filters/Drawers Mobile"
      >
        <div 
          className="bg-white box-border content-stretch flex flex-col gap-[12px] items-start pb-[12px] pt-0 px-0 relative rounded-t-[12px] w-full h-full"
          style={{ boxShadow: '0px -5px 10px 0px rgba(0,0,0,0.15)' }}
        >
          {/* Header */}
          <div 
            className="relative shrink-0 w-full"
            style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
          >
            <div className="flex flex-row items-center size-full">
              <div className="box-border content-stretch flex items-center justify-between px-[10px] py-[12px] relative text-nowrap w-full">
                <h3 
                  className="flex-1 text-center leading-[normal]"
                  style={{ 
                    fontFamily: 'Cabin',
                    fontSize: 'var(--text-h2)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--high-emphasis-font)'
                  }}
                >
                  Sort By
                </h3>
                <button
                  onClick={onClose}
                  className="leading-[normal] relative shrink-0 p-[8px] rounded-[var(--radius)] hover:bg-button-hover active:bg-button-active transition-colors"
                  aria-label="Close"
                >
                  <X size={24} style={{ color: 'var(--high-emphasis-font)' }} />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="relative shrink-0 w-full">
            <div className="size-full">
              <div className="box-border content-stretch flex flex-col gap-[28px] items-start px-[16px] py-0 relative w-full">
                <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
                  <div className="content-stretch flex flex-col items-start justify-center relative shrink-0 w-full">
                    {/* Procedure Dropdown */}
                    <ProcedureDropdown
                      selectedValue={procedureValue}
                      onChange={handleProcedureChange}
                      procedureOptions={procedureOptions}
                    />

                    {/* Sort Options */}
                    {sortOptions.map((option, index) => (
                      <button
                        data-testid={`sort-option-${index}`}
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        className="w-full text-left"
                      >
                        <RadioOption
                          label={option.label}
                          isSelected={sortValue === option.value}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Version - Right slide-in panel (larger than 744px) */}
      <div 
        className="fixed top-0 right-0 bottom-0 z-50 hidden lg:block transition-transform duration-300 ease-out w-[320px]"
        style={{ 
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
        data-name="Sort By Dropdown Filters/Drawers Desktop"
      >
        <div 
          className="bg-white size-full"
          style={{ boxShadow: '-4px 4px 10px 0px rgba(0,0,0,0.1)' }}
        >
          <div className="size-full">
            <div className="box-border content-stretch flex flex-col gap-[16px] items-start p-[16px] relative size-full">
              <div className="basis-0 content-stretch flex flex-col gap-[24px] grow items-start min-h-px min-w-px relative shrink-0 w-full">
                {/* Title */}
                <div className="flex flex-col justify-center leading-[0] relative shrink-0 text-nowrap">
                  <p 
                    className="leading-[normal] whitespace-pre"
                    style={{ 
                      fontFamily: 'Cabin',
                      fontSize: 'var(--text-h3)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--high-emphasis-font)'
                    }}
                  >
                    Sort by
                  </p>
                </div>

                {/* Procedure Dropdown and Sort Options */}
                <div className="content-stretch flex flex-col gap-[16px] items-start relative shrink-0 w-full">
                  <ProcedureDropdown
                    selectedValue={procedureValue}
                    onChange={handleProcedureChange}
                    procedureOptions={procedureOptions}
                  />
                  
                  {/* Sort Options with spacing */}
                  <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        className="w-full text-left"
                      >
                        <RadioOption
                          label={option.label}
                          isSelected={sortValue === option.value}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Close Button */}
                <button 
                  onClick={onClose}
                  className="absolute block cursor-pointer overflow-clip size-[38px] rounded-[var(--radius)] hover:bg-button-hover active:bg-button-active transition-colors"
                  style={{ 
                    left: '253px',
                    top: '-16px'
                  }}
                  aria-label="Close"
                  data-name="Close Button"
                >
                  <div className="absolute left-[10px] size-[20px] top-[10px]" data-name="Close Button Icon">
                    <X 
                      className="absolute inset-0" 
                      size={20} 
                      style={{ color: 'var(--high-emphasis-font)' }}
                    />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
